import { create } from "zustand";
import { subscribeWithSelector, persist } from "zustand/middleware";
import type {
  RoomConfig,
  WallSegment,
  StructuralElement,
  PlacedVolume,
  Point2D,
  Point3D,
  EditorTool,
  ViewMode,
  StructuralElementType,
  KitchenZone,
  ProductResponse,
  VolumeSemantics,
  OccupancyCell,
  PlacementValidation,
  LayoutSuggestion,
  ConflictDetectionResult,
  Conflict,
  StylePalette,
  StylePresetName,
  SnapConfig,
  SnapResult,
  SnapGuideLine,
} from "@byk/shared";
import { DEFAULT_VOLUME_SEMANTICS, DEFAULT_SNAP_CONFIG } from "@byk/shared";
import {
  type OccupancyGrid,
  buildOccupancyGrid,
  validatePlacement,
  getCellAt as getOccupancyCell,
  getAvailableCells as getAvailableOccupancyCells,
} from "../services/occupancy";
import {
  detectConflicts,
  getConflictsForVolume,
} from "../services/conflictDetection";
import { getPresetByName } from "../services/stylePalette";
import { calculateSnap } from "../services/snapping";

// =============================================================================
// Mock Products (until backend integration)
// =============================================================================

export const MOCK_PRODUCTS: ProductResponse[] = [
  {
    id: "p1",
    sku: "NIGA-SINK-DUAL",
    name: "Niga Dual Basin Kosher",
    description: "Dual basin sink for kosher kitchens",
    dimensions: { width: 80, height: 25, depth: 50, unit: "cm" },
    glbKey: null,
    textureKey: null,
    thumbnailKey: null,
    price: 2400,
    currency: "ILS",
    active: true,
    featured: true,
    vendorId: "niga",
    categoryId: "sinks",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "p2",
    sku: "TEKA-OVEN-PYRO",
    name: "Teka Pyrolytic Oven",
    description: "Built-in pyrolytic oven with steam",
    dimensions: { width: 60, height: 60, depth: 55, unit: "cm" },
    glbKey: null,
    textureKey: null,
    thumbnailKey: null,
    price: 4500,
    currency: "ILS",
    active: true,
    featured: true,
    vendorId: "teka",
    categoryId: "ovens",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "p3",
    sku: "TEKA-COOKTOP-IND",
    name: "Teka Induction Cooktop",
    description: "4-zone induction cooktop",
    dimensions: { width: 60, height: 5, depth: 52, unit: "cm" },
    glbKey: null,
    textureKey: null,
    thumbnailKey: null,
    price: 3200,
    currency: "ILS",
    active: true,
    featured: false,
    vendorId: "teka",
    categoryId: "cooktops",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "p4",
    sku: "NIGA-TAP-PULL",
    name: "Niga Pull-Out Tap",
    description: "Professional pull-out kitchen tap",
    dimensions: { width: 10, height: 40, depth: 25, unit: "cm" },
    glbKey: null,
    textureKey: null,
    thumbnailKey: null,
    price: 1800,
    currency: "ILS",
    active: true,
    featured: false,
    vendorId: "niga",
    categoryId: "taps",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "p5",
    sku: "NEOLITH-SURFACE-ESTATUARIO",
    name: "Neolith Estatuario",
    description: "Sintered stone surface - marble look",
    dimensions: { width: 320, height: 3, depth: 160, unit: "cm" },
    glbKey: null,
    textureKey: null,
    thumbnailKey: null,
    price: 8500,
    currency: "ILS",
    active: true,
    featured: true,
    vendorId: "neolith",
    categoryId: "surfaces",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "p6",
    sku: "CABINET-BASE-60",
    name: "Base Cabinet 60cm",
    description: "Standard base cabinet unit",
    dimensions: { width: 60, height: 72, depth: 56, unit: "cm" },
    glbKey: null,
    textureKey: null,
    thumbnailKey: null,
    price: 1200,
    currency: "ILS",
    active: true,
    featured: false,
    vendorId: "generic",
    categoryId: "cabinets",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "p7",
    sku: "CABINET-WALL-80",
    name: "Wall Cabinet 80cm",
    description: "Wall mounted cabinet",
    dimensions: { width: 80, height: 70, depth: 35, unit: "cm" },
    glbKey: null,
    textureKey: null,
    thumbnailKey: null,
    price: 900,
    currency: "ILS",
    active: true,
    featured: false,
    vendorId: "generic",
    categoryId: "cabinets",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "p8",
    sku: "NIGA-PURIFIER-PRO",
    name: "Niga Water Purifier",
    description: "Under-sink water purification system",
    dimensions: { width: 30, height: 40, depth: 15, unit: "cm" },
    glbKey: null,
    textureKey: null,
    thumbnailKey: null,
    price: 2200,
    currency: "ILS",
    active: true,
    featured: false,
    vendorId: "niga",
    categoryId: "purifiers",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "p9",
    sku: "TEKA-FRIDGE-BUILTIN",
    name: "Teka Built-in Refrigerator",
    description: "Integrated refrigerator with freezer",
    dimensions: { width: 60, height: 178, depth: 55, unit: "cm" },
    glbKey: null,
    textureKey: null,
    thumbnailKey: null,
    price: 6500,
    currency: "ILS",
    active: true,
    featured: true,
    vendorId: "teka",
    categoryId: "fridges",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// =============================================================================
// Category to Semantics Mapping (for AI awareness and conflict detection)
// =============================================================================

/**
 * Maps product categories to their semantic properties.
 * These properties are used for:
 * - Work triangle calculations (sink ↔ fridge ↔ stove)
 * - Safety rule enforcement (heat source clearances)
 * - Kashrut compliance (water source separation)
 * - Door swing clearance calculations
 */
export const CATEGORY_SEMANTICS: Record<string, Partial<VolumeSemantics>> = {
  sinks: {
    isWaterSource: true,
    isCounterMountable: true,
    requiresFrontAccess: true,
    frontAccessDepth: 60, // cm for comfortable use
  },
  ovens: {
    isHeatSource: true,
    requiresFrontAccess: true,
    frontAccessDepth: 90, // cm for door swing + safety
  },
  cooktops: {
    isHeatSource: true,
    requiresVentilation: true,
    isCounterMountable: true,
  },
  taps: {
    isWaterSource: true,
    isCounterMountable: true,
  },
  surfaces: {
    isCounterMountable: false, // Surfaces ARE the counter
  },
  cabinets: {
    requiresFrontAccess: true,
    frontAccessDepth: 60, // cm for door/drawer access
    isFloorStanding: true,
  },
  purifiers: {
    isWaterSource: true,
  },
  refrigerators: {
    isColdStorage: true,
    requiresFrontAccess: true,
    frontAccessDepth: 90, // cm for door swing
    isFloorStanding: true,
  },
  fridges: {
    isColdStorage: true,
    requiresFrontAccess: true,
    frontAccessDepth: 90, // cm for door swing
    isFloorStanding: true,
  },
  dishwashers: {
    isWaterSource: true,
    requiresFrontAccess: true,
    frontAccessDepth: 70, // cm for door swing
  },
};

/**
 * Gets the complete semantics for a product category.
 * Merges category-specific overrides with defaults.
 */
export function getSemanticsForCategory(categoryId: string): VolumeSemantics {
  const overrides = CATEGORY_SEMANTICS[categoryId] ?? {};
  return { ...DEFAULT_VOLUME_SEMANTICS, ...overrides };
}

// =============================================================================
// BYK-3: Undo/Redo History Types
// =============================================================================

/**
 * Snapshot of design state for undo/redo.
 * Only tracks entities that can be modified by user actions.
 */
interface HistorySnapshot {
  walls: WallSegment[];
  elements: StructuralElement[];
  volumes: PlacedVolume[];
  room: RoomConfig;
  timestamp: number;
}

const MAX_HISTORY_SIZE = 50; // Limit history to prevent memory issues

// =============================================================================
// Store Interface
// =============================================================================

interface FloorPlanState {
  // Room configuration
  room: RoomConfig;
  walls: WallSegment[];
  elements: StructuralElement[];
  volumes: PlacedVolume[];

  // Selection state
  selectedId: string | null;
  hoveredId: string | null;

  // Editor state
  activeTool: EditorTool;
  viewMode: ViewMode;
  isDrawing: boolean;
  drawStart: Point2D | null;

  // Grid settings
  gridSize: number; // cm
  snapToGrid: boolean;

  // Products catalog
  products: ProductResponse[];
  draggedProductId: string | null;

  // Occupancy grid (not persisted - recalculated on demand)
  occupancyGrid: OccupancyGrid;
  occupancyVersion: number; // Increments on each update for React reactivity

  // Placement preview state
  placementPreview: {
    position: Point3D | null;
    productId: string | null;
    isValid: boolean;
    validation: PlacementValidation | null;
  };

  // Click-to-Place mode (WCAG 2.2 accessibility)
  placementMode: "drag" | "click";
  pendingPlacement: {
    productId: string;
  } | null;

  // AI Panel state
  showAiPanel: boolean;

  // BYK-6: AI Render Panel state
  showRenderPanel: boolean;

  // BYK-17: Style Palette state
  activeStylePalette: StylePalette | null;
  stylePresetName: StylePresetName;

  // BYK-22: Conflict Detection state
  conflictResult: ConflictDetectionResult;
  showConflictsPanel: boolean;

  // BYK-13: BOM Panel state
  showBomPanel: boolean;

  // BYK-32: Enhanced Snapping state
  snapConfig: SnapConfig;
  snapGuideLines: SnapGuideLine[];
  isDragging: boolean;

  // BYK-39: View Transition state
  isViewTransitioning: boolean;
  previousViewMode: ViewMode | null;

  // BYK-34: Floor/Ceiling visibility
  showCeiling: boolean;

  // BYK-3: Undo/Redo state
  historyPast: HistorySnapshot[];
  historyFuture: HistorySnapshot[];
  canUndo: boolean;
  canRedo: boolean;

  // Room Actions
  setRoom: (room: Partial<RoomConfig>) => void;
  resetRoom: () => void;

  // Wall Actions
  addWall: (start: Point2D, end: Point2D) => void;
  updateWall: (id: string, updates: Partial<WallSegment>) => void;
  removeWall: (id: string) => void;
  generateRectangularWalls: () => void;

  // Element Actions
  addElement: (
    wallId: string,
    type: StructuralElementType,
    offset: number,
    width: number,
    height: number,
    elevation: number
  ) => void;
  updateElement: (id: string, updates: Partial<StructuralElement>) => void;
  removeElement: (id: string) => void;

  // Volume Actions
  addVolume: (productId: string, position: Point3D, zone?: KitchenZone) => void;
  updateVolume: (id: string, updates: Partial<PlacedVolume>) => void;
  moveVolume: (id: string, position: Point3D) => void;
  rotateVolume: (id: string, rotation: number) => void;
  removeVolume: (id: string) => void;

  // Selection Actions
  select: (id: string | null) => void;
  setHovered: (id: string | null) => void;

  // Editor Actions
  setTool: (tool: EditorTool) => void;
  setViewMode: (mode: ViewMode) => void;
  setDrawing: (isDrawing: boolean, start?: Point2D) => void;
  setGridSize: (size: number) => void;
  toggleSnapToGrid: () => void;

  // Product Actions
  setDraggedProduct: (productId: string | null) => void;
  getProductById: (id: string) => ProductResponse | undefined;

  // Utility
  snapPoint: (point: Point2D) => Point2D;
  clearAll: () => void;

  // Occupancy Actions
  updateOccupancy: () => void;
  getCellAt: (x: number, y: number) => OccupancyCell;
  isPlacementValid: (
    productId: string,
    position: Point3D,
    excludeVolumeId?: string
  ) => PlacementValidation;
  getAvailableCells: () => Point2D[];
  setPlacementPreview: (
    position: Point3D | null,
    productId: string | null
  ) => void;
  clearPlacementPreview: () => void;

  // AI Layout Actions
  applyLayoutSuggestion: (layout: LayoutSuggestion) => void;
  toggleAiPanel: () => void;
  setShowAiPanel: (show: boolean) => void;

  // BYK-6: AI Render Panel Actions
  toggleRenderPanel: () => void;
  setShowRenderPanel: (show: boolean) => void;

  // Click-to-Place Actions (WCAG 2.2)
  setPlacementMode: (mode: "drag" | "click") => void;
  startClickPlacement: (productId: string) => void;
  confirmClickPlacement: (position: Point3D) => void;
  cancelPlacement: () => void;

  // BYK-17: Style Palette Actions
  setStylePalette: (palette: StylePalette) => void;
  getActiveStylePalette: () => StylePalette | null;

  // BYK-22: Conflict Detection Actions
  updateConflicts: () => void;
  getConflictsForVolume: (volumeId: string) => Conflict[];
  toggleConflictsPanel: () => void;
  setShowConflictsPanel: (show: boolean) => void;

  // BYK-13: BOM Panel Actions
  toggleBomPanel: () => void;
  setShowBomPanel: (show: boolean) => void;

  // BYK-32: Enhanced Snapping Actions
  setSnapConfig: (config: Partial<SnapConfig>) => void;
  toggleWallSnap: () => void;
  toggleCabinetSnap: () => void;
  calculateSnapPosition: (
    position: Point2D,
    productId: string,
    currentVolumeId?: string | null
  ) => SnapResult;
  setDragging: (isDragging: boolean) => void;
  clearSnapGuides: () => void;

  // BYK-39: View Transition Actions
  setViewModeWithTransition: (mode: ViewMode) => void;
  onViewTransitionComplete: () => void;

  // BYK-34: Floor/Ceiling Actions
  toggleCeiling: () => void;

  // BYK-3: Undo/Redo Actions
  undo: () => void;
  redo: () => void;
  pushToHistory: () => void;
  clearHistory: () => void;
}

// =============================================================================
// Default Values
// =============================================================================

const DEFAULT_ROOM: RoomConfig = {
  width: 400, // 4m
  depth: 300, // 3m
  height: 270, // 2.7m
};

const DEFAULT_CONFLICT_RESULT: ConflictDetectionResult = {
  conflicts: [],
  errorCount: 0,
  warningCount: 0,
  infoCount: 0,
  isValid: true,
};

// =============================================================================
// Store Implementation
// =============================================================================

export const useFloorPlanStore = create<FloorPlanState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // Initial state
        room: DEFAULT_ROOM,
        walls: [],
        elements: [],
        volumes: [],
        selectedId: null,
        hoveredId: null,
        activeTool: "select",
        viewMode: "split",
        isDrawing: false,
        drawStart: null,
        gridSize: 10, // 10cm grid
        snapToGrid: true,
        products: MOCK_PRODUCTS,
        draggedProductId: null,

        // Occupancy grid (computed, not persisted)
        occupancyGrid: new Map(),
        occupancyVersion: 0,

        // Placement preview
        placementPreview: {
          position: null,
          productId: null,
          isValid: true,
          validation: null,
        },

        // Click-to-Place mode (WCAG 2.2)
        placementMode: "drag",
        pendingPlacement: null,

        // AI Panel state
        showAiPanel: false,

        // BYK-6: AI Render Panel state
        showRenderPanel: false,

        // BYK-17: Style Palette state
        activeStylePalette: getPresetByName("warm-2025"),
        stylePresetName: "warm-2025" as StylePresetName,

        // BYK-22: Conflict Detection state
        conflictResult: DEFAULT_CONFLICT_RESULT,
        showConflictsPanel: false,

        // BYK-13: BOM Panel state
        showBomPanel: false,

        // BYK-32: Enhanced Snapping state
        snapConfig: DEFAULT_SNAP_CONFIG,
        snapGuideLines: [],
        isDragging: false,

        // BYK-39: View Transition state
        isViewTransitioning: false,
        previousViewMode: null,

        // BYK-34: Floor/Ceiling visibility
        showCeiling: true,

        // BYK-3: Undo/Redo state
        historyPast: [],
        historyFuture: [],
        canUndo: false,
        canRedo: false,

        // Room Actions
        setRoom: (roomUpdate) =>
          set((state) => ({
            room: { ...state.room, ...roomUpdate },
          })),

        resetRoom: () => set({ room: DEFAULT_ROOM }),

        // Wall Actions
        addWall: (start, end) => {
          get().pushToHistory();
          set((state) => {
            const snappedStart = state.snapToGrid
              ? get().snapPoint(start)
              : start;
            const snappedEnd = state.snapToGrid ? get().snapPoint(end) : end;

            const newWall: WallSegment = {
              id: crypto.randomUUID(),
              start: snappedStart,
              end: snappedEnd,
              thickness: 15,
              height: state.room.height,
            };
            return { walls: [...state.walls, newWall] };
          });
        },

        updateWall: (id, updates) => {
          get().pushToHistory();
          set((state) => ({
            walls: state.walls.map((w) =>
              w.id === id ? { ...w, ...updates } : w
            ),
          }));
        },

        removeWall: (id) => {
          get().pushToHistory();
          set((state) => ({
            walls: state.walls.filter((w) => w.id !== id),
            elements: state.elements.filter((e) => e.wallId !== id),
          }));
        },

        generateRectangularWalls: () => {
          get().pushToHistory();
          set((state) => {
            const { width, depth, height } = state.room;
            const walls: WallSegment[] = [
              // North wall (top)
              {
                id: "wall-north",
                start: { x: 0, y: 0 },
                end: { x: width, y: 0 },
                thickness: 15,
                height,
              },
              // East wall (right)
              {
                id: "wall-east",
                start: { x: width, y: 0 },
                end: { x: width, y: depth },
                thickness: 15,
                height,
              },
              // South wall (bottom)
              {
                id: "wall-south",
                start: { x: width, y: depth },
                end: { x: 0, y: depth },
                thickness: 15,
                height,
              },
              // West wall (left)
              {
                id: "wall-west",
                start: { x: 0, y: depth },
                end: { x: 0, y: 0 },
                thickness: 15,
                height,
              },
            ];
            return { walls };
          });
        },

        // Element Actions
        addElement: (wallId, type, offset, width, height, elevation) => {
          get().pushToHistory();
          set((state) => {
            const newElement: StructuralElement = {
              id: crypto.randomUUID(),
              type,
              wallId,
              offset,
              width,
              height,
              elevation,
            };
            return { elements: [...state.elements, newElement] };
          });
        },

        updateElement: (id, updates) => {
          get().pushToHistory();
          set((state) => ({
            elements: state.elements.map((e) =>
              e.id === id ? { ...e, ...updates } : e
            ),
          }));
        },

        removeElement: (id) => {
          get().pushToHistory();
          set((state) => ({
            elements: state.elements.filter((e) => e.id !== id),
          }));
        },

        // Volume Actions
        addVolume: (productId, position, zone = "prep") => {
          get().pushToHistory();
          set((state) => {
            const snappedPosition = state.snapToGrid
              ? {
                  ...get().snapPoint({ x: position.x, y: position.y }),
                  z: position.z,
                }
              : position;

            // Look up product to get category for semantic mapping
            const product = state.products.find((p) => p.id === productId);
            const semantics = product
              ? getSemanticsForCategory(product.categoryId)
              : DEFAULT_VOLUME_SEMANTICS;

            const newVolume: PlacedVolume = {
              id: crypto.randomUUID(),
              productId,
              position: snappedPosition,
              rotation: 0,
              zone,
              semantics,
            };
            return { volumes: [...state.volumes, newVolume] };
          });
        },

        updateVolume: (id, updates) => {
          get().pushToHistory();
          set((state) => ({
            volumes: state.volumes.map((v) =>
              v.id === id ? { ...v, ...updates } : v
            ),
          }));
        },

        moveVolume: (id, position) => {
          get().pushToHistory();
          set((state) => {
            const snappedPosition = state.snapToGrid
              ? {
                  ...get().snapPoint({ x: position.x, y: position.y }),
                  z: position.z,
                }
              : position;

            return {
              volumes: state.volumes.map((v) =>
                v.id === id ? { ...v, position: snappedPosition } : v
              ),
            };
          });
        },

        rotateVolume: (id, rotation) => {
          get().pushToHistory();
          set((state) => ({
            volumes: state.volumes.map((v) =>
              v.id === id ? { ...v, rotation: rotation % 360 } : v
            ),
          }));
        },

        removeVolume: (id) => {
          get().pushToHistory();
          set((state) => ({
            volumes: state.volumes.filter((v) => v.id !== id),
            selectedId: state.selectedId === id ? null : state.selectedId,
          }));
        },

        // Selection Actions
        select: (id) => set({ selectedId: id }),
        setHovered: (id) => set({ hoveredId: id }),

        // Editor Actions
        setTool: (tool) => set({ activeTool: tool, isDrawing: false, drawStart: null }),
        setViewMode: (mode) => set({ viewMode: mode }),
        setDrawing: (isDrawing, start) =>
          set({ isDrawing, drawStart: start ?? null }),
        setGridSize: (size) => set({ gridSize: Math.max(1, size) }),
        toggleSnapToGrid: () => set((state) => ({ snapToGrid: !state.snapToGrid })),

        // Product Actions
        setDraggedProduct: (productId) => set({ draggedProductId: productId }),
        getProductById: (id) => get().products.find((p) => p.id === id),

        // Utility
        snapPoint: (point) => {
          const { gridSize } = get();
          return {
            x: Math.round(point.x / gridSize) * gridSize,
            y: Math.round(point.y / gridSize) * gridSize,
          };
        },

        clearAll: () =>
          set({
            walls: [],
            elements: [],
            volumes: [],
            selectedId: null,
            occupancyGrid: new Map(),
            occupancyVersion: 0,
          }),

        // Occupancy Actions
        updateOccupancy: () => {
          const { room, walls, volumes, products, gridSize } = get();
          const grid = buildOccupancyGrid({
            room,
            walls,
            volumes,
            products,
            cellSize: gridSize,
          });
          set((state) => ({
            occupancyGrid: grid,
            occupancyVersion: state.occupancyVersion + 1,
          }));
        },

        getCellAt: (x, y) => {
          const { occupancyGrid, gridSize } = get();
          return getOccupancyCell(occupancyGrid, x, y, gridSize);
        },

        isPlacementValid: (productId, position, excludeVolumeId) => {
          const { occupancyGrid, products, gridSize } = get();
          const product = products.find((p) => p.id === productId);

          if (!product) {
            return { valid: false, reason: "Product not found" };
          }

          if (!product.dimensions) {
            return { valid: false, reason: "Product has no dimensions" };
          }

          // Get semantics for the product
          const semantics = getSemanticsForCategory(product.categoryId);

          return validatePlacement(
            occupancyGrid,
            excludeVolumeId || null,
            position,
            {
              width: product.dimensions.width,
              height: product.dimensions.height,
              depth: product.dimensions.depth,
            },
            0, // default rotation
            semantics,
            gridSize
          );
        },

        getAvailableCells: () => {
          const { occupancyGrid, room, gridSize } = get();
          return getAvailableOccupancyCells(occupancyGrid, room, gridSize);
        },

        setPlacementPreview: (position, productId) => {
          if (!position || !productId) {
            set({
              placementPreview: {
                position: null,
                productId: null,
                isValid: true,
                validation: null,
              },
            });
            return;
          }

          const validation = get().isPlacementValid(productId, position);
          set({
            placementPreview: {
              position,
              productId,
              isValid: validation.valid,
              validation,
            },
          });
        },

        clearPlacementPreview: () =>
          set({
            placementPreview: {
              position: null,
              productId: null,
              isValid: true,
              validation: null,
            },
          }),

        // AI Layout Actions
        applyLayoutSuggestion: (layout) => {
          const { products } = get();

          // Convert layout placements to volumes
          const newVolumes: PlacedVolume[] = layout.placements.map(
            (placement) => {
              // Find product to get semantics
              const product = products.find(
                (p) => p.id === placement.productId
              );
              const semantics = product
                ? getSemanticsForCategory(product.categoryId)
                : DEFAULT_VOLUME_SEMANTICS;

              // Determine zone based on role
              const zoneMap: Record<string, KitchenZone> = {
                sink: "cleaning",
                fridge: "storage",
                cooktop: "cooking",
              };

              return {
                id: crypto.randomUUID(),
                productId: placement.productId,
                position: placement.position,
                rotation: placement.rotation,
                zone: zoneMap[placement.role] || "prep",
                semantics,
              };
            }
          );

          // Add volumes to state
          set((state) => ({
            volumes: [...state.volumes, ...newVolumes],
          }));

          // Update occupancy grid
          get().updateOccupancy();
        },

        toggleAiPanel: () => set((state) => ({ showAiPanel: !state.showAiPanel })),

        setShowAiPanel: (show) => set({ showAiPanel: show }),

        // BYK-6: AI Render Panel Actions
        toggleRenderPanel: () => set((state) => ({ showRenderPanel: !state.showRenderPanel })),

        setShowRenderPanel: (show) => set({ showRenderPanel: show }),

        // Click-to-Place Actions (WCAG 2.2)
        setPlacementMode: (mode) => set({ placementMode: mode, pendingPlacement: null }),

        startClickPlacement: (productId) =>
          set((state) => {
            // Only start if in click mode
            if (state.placementMode !== "click") return state;
            return { pendingPlacement: { productId } };
          }),

        confirmClickPlacement: (position) => {
          const { pendingPlacement, snapToGrid, snapPoint, products } = get();
          if (!pendingPlacement) return;

          const snappedPosition = snapToGrid
            ? { ...snapPoint({ x: position.x, y: position.y }), z: position.z }
            : position;

          // Look up product to get category for semantic mapping
          const product = products.find((p) => p.id === pendingPlacement.productId);
          const semantics = product
            ? getSemanticsForCategory(product.categoryId)
            : DEFAULT_VOLUME_SEMANTICS;

          const newVolume: PlacedVolume = {
            id: crypto.randomUUID(),
            productId: pendingPlacement.productId,
            position: snappedPosition,
            rotation: 0,
            zone: "prep",
            semantics,
          };

          set((state) => ({
            volumes: [...state.volumes, newVolume],
            pendingPlacement: null,
          }));
        },

        cancelPlacement: () => set({ pendingPlacement: null }),

        // BYK-17: Style Palette Actions
        setStylePalette: (palette) => {
          set({
            stylePresetName: palette.presetName || "warm-2025",
            activeStylePalette: palette,
          });
        },

        getActiveStylePalette: () => get().activeStylePalette,

        // BYK-22: Conflict Detection Actions
        updateConflicts: () => {
          const { room, walls, elements, volumes, products, gridSize } = get();
          const result = detectConflicts({
            room,
            walls,
            elements,
            volumes,
            products,
            cellSize: gridSize,
          });
          set({ conflictResult: result });
        },

        getConflictsForVolume: (volumeId) => {
          const { conflictResult } = get();
          return getConflictsForVolume(conflictResult.conflicts, volumeId);
        },

        toggleConflictsPanel: () =>
          set((state) => ({ showConflictsPanel: !state.showConflictsPanel })),

        setShowConflictsPanel: (show) => set({ showConflictsPanel: show }),

        // BYK-13: BOM Panel Actions
        toggleBomPanel: () =>
          set((state) => ({ showBomPanel: !state.showBomPanel })),

        setShowBomPanel: (show) => set({ showBomPanel: show }),

        // BYK-32: Enhanced Snapping Actions
        setSnapConfig: (configUpdate) =>
          set((state) => ({
            snapConfig: { ...state.snapConfig, ...configUpdate },
          })),

        toggleWallSnap: () =>
          set((state) => ({
            snapConfig: {
              ...state.snapConfig,
              wallEnabled: !state.snapConfig.wallEnabled,
            },
          })),

        toggleCabinetSnap: () =>
          set((state) => ({
            snapConfig: {
              ...state.snapConfig,
              cabinetEnabled: !state.snapConfig.cabinetEnabled,
            },
          })),

        calculateSnapPosition: (position, productId, currentVolumeId = null) => {
          const { room, walls, volumes, products, snapConfig } = get();
          const product = products.find((p) => p.id === productId);

          if (!product?.dimensions) {
            return {
              position,
              didSnap: false,
              targets: [],
              activeTarget: null,
              guideLines: [],
            };
          }

          const result = calculateSnap({
            position,
            objectWidth: product.dimensions.width,
            objectDepth: product.dimensions.depth,
            room,
            walls,
            volumes,
            products,
            currentVolumeId,
            config: snapConfig,
          });

          // Update guide lines for rendering
          set({ snapGuideLines: result.guideLines });

          return result;
        },

        setDragging: (isDragging) => {
          set({ isDragging });
          if (!isDragging) {
            // Clear guide lines when dragging ends
            set({ snapGuideLines: [] });
          }
        },

        clearSnapGuides: () => set({ snapGuideLines: [] }),

        // BYK-39: View Transition Actions
        setViewModeWithTransition: (mode) => {
          const currentMode = get().viewMode;
          if (currentMode === mode) return;

          // Start transition - store previous mode for animation
          set({
            isViewTransitioning: true,
            previousViewMode: currentMode,
            viewMode: mode,
          });
        },

        onViewTransitionComplete: () => {
          set({
            isViewTransitioning: false,
            previousViewMode: null,
          });
        },

        // BYK-34: Floor/Ceiling Actions
        toggleCeiling: () => set((state) => ({ showCeiling: !state.showCeiling })),

        // BYK-3: Undo/Redo Actions
        pushToHistory: () => {
          const { room, walls, elements, volumes, historyPast } = get();
          const snapshot: HistorySnapshot = {
            room: { ...room },
            walls: JSON.parse(JSON.stringify(walls)),
            elements: JSON.parse(JSON.stringify(elements)),
            volumes: JSON.parse(JSON.stringify(volumes)),
            timestamp: Date.now(),
          };
          const newPast = [...historyPast, snapshot].slice(-MAX_HISTORY_SIZE);
          set({
            historyPast: newPast,
            historyFuture: [], // Clear redo stack on new action
            canUndo: true,
            canRedo: false,
          });
        },

        undo: () => {
          const { room, walls, elements, volumes, historyPast, historyFuture } = get();
          if (historyPast.length === 0) return;

          // Save current state to future (redo) stack
          const currentSnapshot: HistorySnapshot = {
            room: { ...room },
            walls: JSON.parse(JSON.stringify(walls)),
            elements: JSON.parse(JSON.stringify(elements)),
            volumes: JSON.parse(JSON.stringify(volumes)),
            timestamp: Date.now(),
          };

          // Pop the last state from past
          const newPast = [...historyPast];
          const previousState = newPast.pop()!;

          set({
            room: previousState.room,
            walls: previousState.walls,
            elements: previousState.elements,
            volumes: previousState.volumes,
            historyPast: newPast,
            historyFuture: [currentSnapshot, ...historyFuture].slice(0, MAX_HISTORY_SIZE),
            canUndo: newPast.length > 0,
            canRedo: true,
          });
        },

        redo: () => {
          const { room, walls, elements, volumes, historyPast, historyFuture } = get();
          if (historyFuture.length === 0) return;

          // Save current state to past (undo) stack
          const currentSnapshot: HistorySnapshot = {
            room: { ...room },
            walls: JSON.parse(JSON.stringify(walls)),
            elements: JSON.parse(JSON.stringify(elements)),
            volumes: JSON.parse(JSON.stringify(volumes)),
            timestamp: Date.now(),
          };

          // Pop the first state from future
          const newFuture = [...historyFuture];
          const nextState = newFuture.shift()!;

          set({
            room: nextState.room,
            walls: nextState.walls,
            elements: nextState.elements,
            volumes: nextState.volumes,
            historyPast: [...historyPast, currentSnapshot].slice(-MAX_HISTORY_SIZE),
            historyFuture: newFuture,
            canUndo: true,
            canRedo: newFuture.length > 0,
          });
        },

        clearHistory: () => {
          set({
            historyPast: [],
            historyFuture: [],
            canUndo: false,
            canRedo: false,
          });
        },
      }),
      {
        name: "byk-floor-plan",
        version: 1, // Increment when schema changes
        partialize: (state) => ({
          room: state.room,
          walls: state.walls,
          elements: state.elements,
          volumes: state.volumes,
          gridSize: state.gridSize,
          snapToGrid: state.snapToGrid,
          placementMode: state.placementMode,
          stylePresetName: state.stylePresetName,
        }),
        // Migration for old localStorage data without semantics
        migrate: (persistedState, version) => {
          const state = persistedState as Partial<FloorPlanState>;

          // Version 0 → 1: Add semantics to volumes
          if (version === 0 && state.volumes) {
            state.volumes = state.volumes.map((volume) => {
              if (!volume.semantics) {
                // Find product to determine category
                const product = MOCK_PRODUCTS.find(
                  (p) => p.id === volume.productId
                );
                return {
                  ...volume,
                  semantics: product
                    ? getSemanticsForCategory(product.categoryId)
                    : DEFAULT_VOLUME_SEMANTICS,
                };
              }
              return volume;
            });
          }

          return state as FloorPlanState;
        },
      }
    )
  )
);

// =============================================================================
// Auto-trigger conflict detection when volumes change (BYK-22)
// =============================================================================

// Subscribe to volume changes and automatically update conflicts
useFloorPlanStore.subscribe(
  (state) => state.volumes,
  () => {
    // Debounce conflict detection to avoid excessive calls during rapid changes
    const store = useFloorPlanStore.getState();
    store.updateConflicts();
  },
  { equalityFn: (a, b) => a === b }
);

// Also trigger on initial load if there are volumes
if (useFloorPlanStore.getState().volumes.length > 0) {
  useFloorPlanStore.getState().updateConflicts();
}

export default useFloorPlanStore;
